import React, { useEffect } from "react";
import {
  alpha,
  Box,
  Chip,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import isPropValid from "@emotion/is-prop-valid";
import { useScrollPosition } from "@rentablo/components";
import { FilterItem, FilterValue } from "../types";

const ChipsWrapper = styled(Box)({
  position: "relative",
  minWidth: 0,
});

const ChipsContainer = styled(Box, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || (prop !== "shouldBeScrollable" && prop !== "size"),
})<{ shouldBeScrollable: boolean; size: "small" | "medium" | "large" }>`
  display: flex;
  flex-wrap: ${({ shouldBeScrollable }) =>
    shouldBeScrollable ? "nowrap" : "wrap"};
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin-right: ${({ shouldBeScrollable, theme }) =>
    shouldBeScrollable ? theme.spacing(-6) : 0};
  padding-right: ${({ shouldBeScrollable, theme }) =>
    shouldBeScrollable ? theme.spacing(6) : 0};
  overflow: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  .MuiChip-root {
    height: ${({ size }) =>
      size === "large" ? "36px" : size === "medium" ? "30px" : "24px"};
  }
`;

// The scrollable container bleeds theme.spacing(6) past the wrapper, so the
// right bar has to follow it out to sit on the actual scroll edge.
const GradientBar = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== "side",
})<{ side: "left" | "right" }>(({ theme, side }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  [side]: side === "right" ? theme.spacing(-6) : 0,
  width: theme.spacing(10),
  pointerEvents: "none",
  background: `linear-gradient(to ${side}, ${alpha(
    theme.palette.background.subtle,
    0
  )} 4%, ${theme.palette.background.subtle} 99%)`,
}));

interface Props {
  options: FilterItem[];
  selectedOptions: FilterValue[];
  setSelectedOptions: (options: FilterValue[]) => void;
  scrollableContainer?: boolean;
  showGradient?: boolean;
  hasAllOption?: boolean;
  singleSelectMode?: boolean;
  infoMode?: boolean;
  size?: "small" | "medium" | "large";
}

export const Chips = ({
  options,
  selectedOptions,
  setSelectedOptions,
  scrollableContainer = false,
  showGradient = false,
  hasAllOption = true,
  singleSelectMode = false,
  infoMode = false,
  size = "large",
}: Props) => {
  const theme = useTheme();
  const isMD = useMediaQuery(theme.breakpoints.down("md"));
  const { ref, data, recalculate } = useScrollPosition();

  useEffect(() => {
    recalculate();
  }, [options, recalculate]);

  const onChipSelect = (value: FilterValue) => {
    if (infoMode) return;

    if (singleSelectMode) {
      if (!selectedOptions.includes(value)) setSelectedOptions([value]);
      return;
    }

    if (value === "all") {
      return setSelectedOptions(["all"]);
    }

    if (selectedOptions.includes(value)) {
      const temp = selectedOptions.filter((chip) => chip !== value);
      return setSelectedOptions(
        temp.length === 0 ? (hasAllOption ? ["all"] : []) : temp
      );
    }
    setSelectedOptions([...selectedOptions.filter((x) => x !== "all"), value]);
  };

  const shouldBeScrollable = scrollableContainer && isMD;
  const withGradient =
    shouldBeScrollable &&
    showGradient &&
    data.hasMeasured &&
    data.isScrollableHorizontally;

  return (
    <ChipsWrapper>
      <ChipsContainer
        ref={ref}
        shouldBeScrollable={shouldBeScrollable}
        size={size}
      >
        {options.map((type) => (
          <Chip
            variant="outlined"
            key={`key-${type.value}`}
            data-testid={type.testId ?? `depots-chip-${type.value}`}
            label={type.text}
            icon={type.icon}
            color={
              !type.clickable || selectedOptions.includes(type.value)
                ? "primary"
                : "secondary"
            }
            onClick={type.clickable ? () => onChipSelect(type.value) : () => {}}
            sx={{
              height: "36px",
              cursor: infoMode ? "default" : "pointer",
            }}
          />
        ))}
      </ChipsContainer>
      {withGradient && !data.isScrolledToStart && <GradientBar side="left" />}
      {withGradient && !data.isScrolledToEnd && <GradientBar side="right" />}
    </ChipsWrapper>
  );
};
